import {
  ExecutionModes,
  VerificationNames,
  VerificationStatus
} from "shared/types";
import { CreditReport, VerificationReport } from "types/landlord/reports";
import { ReportHeader } from "../../components/ReportHeader";
import { DefinedQueryObserverResult, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { ApplicantSettings } from "shared/types";
import { Applicant, Application } from "types/application";
import { Tabs, type Tab } from "shared/components/BaseTabs";
import { Badge, BadgeSize, BadgeVariant } from "shared/components";
import { HugeiconsIcon } from "@hugeicons/react";
import { Forward02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { CreditReportByStatus } from "../CreditReportSlice/components/CreditReportByStatus";
import api from "api/landlord/applications";

interface CanadianCreditReportSliceProps {
  applicant: Applicant;
  defaultVerificationId: number;
  isNotResident: boolean;
  screeningCriteria?: ApplicantSettings;
  applicationMeta: DefinedQueryObserverResult<Application | undefined, unknown>;
  isIdentityVerificationOverrideRequired: boolean;
}

export const CanadianCreditReportSlice = ({
  applicant,
  defaultVerificationId,
  isNotResident,
  screeningCriteria,
  applicationMeta,
  isIdentityVerificationOverrideRequired
}: CanadianCreditReportSliceProps) => {
  const [openRerunModal, setOpenRerunModal] = useState(false);
  const [selectedVerificationId, setSelectedVerificationId] = useState<number>(
    defaultVerificationId
  );

  const allCreditVerifications = applicant.canadian_credit_report_verifications;
  const selectedVerification = allCreditVerifications?.find(
    (cr) => cr.id === selectedVerificationId
  );

  const tabs: Tab<number>[] = (allCreditVerifications ?? []).map((cr, idx) => {
    const reportNumber = (allCreditVerifications?.length ?? 0) - idx;
    return {
      value: cr.id,
      label: (
        <span className="flex items-center gap-2">
          Report {reportNumber}
          <Badge
            className="flex items-center capitalize"
            size={BadgeSize.SM}
            variant={!cr.archived_at ? BadgeVariant.SUCCESS : BadgeVariant.BASE}
          >
            {!cr.archived_at ? "Actual" : "Archive"}
          </Badge>
        </span>
      )
    };
  });

  const activeCreditReport = allCreditVerifications?.find(
    (cr) => !cr.archived_at
  );

  const { data: verificationCreditReport, isFetched } = useQuery<
    VerificationReport<CreditReport>
  >({
    queryKey: [
      "CANADIAN_CREDIT_REPORT",
      applicant.application_id,
      selectedVerificationId,
      applicant.id
    ],
    queryFn: () =>
      api
        .getApplicantVerification(
          applicant.application_id,
          applicant.id,
          selectedVerificationId
        )
        .then((res) => res.verification)
  });

  const disabledRerunModalTooltip = useMemo(() => {
    if (activeCreditReport?.id !== selectedVerificationId)
      return "Please select the latest credit report to re-run";
    if (
      activeCreditReport?.status === VerificationStatus.Pending ||
      activeCreditReport?.status === VerificationStatus.Processing
    )
      return "Cannot re-run credit report until active report finishes";
  }, [isFetched, activeCreditReport, selectedVerificationId]);

  if (isNotResident)
    return (
      <div className="w-full h-full flex flex-col items-center justify-center gap-6">
        <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center">
          <HugeiconsIcon
            icon={Forward02Icon}
            size={24}
            className="text-primary-500"
          />
        </div>
        <div className="text-center max-w-[432px] space-y-2">
          <p className="text-content-1000 text-base font-bold">
            Credit report was skipped
          </p>
          <p className="text-content-300 text-sm font-medium">
            Applicant is not a Canadian resident and doesn&apos;t have a Social
            Insurance Number and Canadian Address
          </p>
        </div>
      </div>
    );

  return (
    <div>
      <ReportHeader
        applicationStatus={applicationMeta.data?.status}
        title="Credit report (CA)"
        applicantId={applicant.id}
        applicationId={applicant.application_id}
        name={VerificationNames.CanadianCreditReport}
        onRerunReport={isFetched ? () => setOpenRerunModal(true) : undefined}
        rerunRequirements={
          isFetched ? selectedVerification?.rerun_requirements : undefined
        }
        hidePrint={
          selectedVerification?.status === VerificationStatus.Pending &&
          selectedVerification.settings?.execution_mode ===
            ExecutionModes.Manual
        }
        disabledRerunModalTooltip={disabledRerunModalTooltip}
      />

      <div className="-mt-2 mb-8 border-b-1 max-w-[90vw] md:max-w-[80vw] lg:max-w-[min(60vw,900px)]">
        {allCreditVerifications && allCreditVerifications.length > 1 && (
          <Tabs
            tabs={tabs}
            active={selectedVerificationId}
            onChange={setSelectedVerificationId}
            size="sm"
            tabStyle="line"
          />
        )}
      </div>

      <CreditReportByStatus
        verificationCreditReport={verificationCreditReport}
        isFetched={isFetched}
        applicant={applicant}
        applicationMeta={applicationMeta}
        screeningCriteria={screeningCriteria}
        selectedVerificationId={selectedVerificationId}
        activeReportId={activeCreditReport?.id}
        openRerunModal={openRerunModal}
        setOpenRerunModal={setOpenRerunModal}
        isIdentityVerificationOverrideRequired={
          isIdentityVerificationOverrideRequired
        }
        isSIN={true}
      />
    </div>
  );
};
